import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Leaf, Search, CheckCircle2, Circle } from "lucide-react";
import { STAGES, STAGE_MAP, fmtDateTime, fmtDate } from "@/lib/cc";
import { toast } from "sonner";

const visible = STAGES.filter((s) => s.key !== "rejected");

export default function PortalTrack() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [apps, setApps] = useState([]);
  const [searched, setSearched] = useState(false);

  const lookup = async () => {
    if (!email) return;
    try {
      const { data } = await api.get("/portal/track", { params: { email } });
      setApps(data);
      setSearched(true);
    } catch { toast.error("Could not look up"); }
  };

  useEffect(() => { if (params.get("email")) lookup(); }, []);

  const respond = async (cid, response) => {
    try {
      await api.post("/portal/offer/respond", { candidate_id: cid, response });
      toast.success(`Offer ${response}`);
      lookup();
    } catch { toast.error("Could not record response"); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--cc-bg)" }}>
      <Seo title="Track your application | Rabah Concept" noindex />
      <header className="cc-topbar px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--cc-primary)" }}>
            <Leaf className="w-4 h-4 text-white" />
          </div>
          <div className="font-display font-bold text-lg">CareConnect</div>
        </Link>
        <Link to="/jobs" className="btn-ghost">Browse jobs</Link>
      </header>

      <div className="max-w-3xl mx-auto px-8 py-16">
        <div className="label-eyebrow mb-3">Application status</div>
        <h1 className="font-display text-5xl font-bold mb-8">Track your application.</h1>

        <div className="cc-card p-6 mb-6">
          <label className="label-eyebrow block mb-1">Your email</label>
          <div className="flex gap-2">
            <input type="email" className="cc-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.co.uk" data-testid="track-email-input" />
            <button className="btn-primary" onClick={lookup} data-testid="track-lookup-button"><Search className="w-4 h-4" /> Look up</button>
          </div>
        </div>

        {searched && apps.length === 0 && (
          <div className="cc-card p-8 text-center text-[var(--cc-text-soft)]">No applications found for that email.</div>
        )}

        {apps.map((a) => (
          <div key={a.id} className="cc-card p-7 mb-4 fade-up">
            <div className="flex justify-between items-start mb-5">
              <div>
                <div className="label-eyebrow mb-1">Application</div>
                <h2 className="font-display text-2xl font-bold">{a.job_title}</h2>
                <div className="text-xs text-[var(--cc-text-soft)] mt-1">Applied {fmtDate(a.applied_at)}</div>
              </div>
              <span className="pill pill-info">{STAGE_MAP[a.stage]}</span>
            </div>

            {/* Stage timeline */}
            <div className="flex items-center justify-between mb-6">
              {visible.map((s, i) => {
                const idx = visible.findIndex((x) => x.key === a.stage);
                const reached = i <= idx;
                return (
                  <div key={s.key} className="flex-1 flex items-center">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${reached ? "bg-[var(--cc-primary)] text-white" : "bg-[var(--cc-surface)] text-[var(--cc-text-soft)] border border-[var(--cc-border)]"}`}>
                      {reached ? "✓" : i + 1}
                    </div>
                    {i < visible.length - 1 && <div className={`flex-1 h-0.5 ${i < idx ? "bg-[var(--cc-primary)]" : "bg-[var(--cc-border)]"}`} />}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-7 text-[10px] text-[var(--cc-text-soft)] mb-6 -mt-3">
              {visible.map((s) => <div key={s.key} className="text-center">{s.label}</div>)}
            </div>

            {/* Offer */}
            {a.offer && a.offer.status === "sent" && (
              <div className="p-4 rounded-xl mb-4" style={{ background: "var(--cc-secondary)" }}>
                <div className="font-semibold text-[var(--cc-primary)] mb-1">You have a {a.offer.type} offer!</div>
                <div className="text-sm text-[var(--cc-primary)]/80 mb-3">{a.offer.salary} · Start date {fmtDate(a.offer.start_date)}</div>
                <div className="flex gap-2">
                  <button className="btn-primary" onClick={() => respond(a.id, "accepted")} data-testid={`offer-accept-${a.id}`}>Accept</button>
                  <button className="btn-secondary" onClick={() => respond(a.id, "declined")} data-testid={`offer-decline-${a.id}`}>Decline</button>
                </div>
              </div>
            )}

            {/* Timeline log */}
            <div>
              <div className="label-eyebrow mb-2">Activity</div>
              <div className="space-y-2">
                {[...(a.timeline || [])].reverse().slice(0, 6).map((t, i) => (
                  <div key={i} className="text-sm flex justify-between gap-4">
                    <span>{t.event}</span>
                    <span className="text-xs text-[var(--cc-text-soft)] whitespace-nowrap">{fmtDateTime(t.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
